import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

type DarkModeCtx = {
  isDarkMode: boolean;
  setIsDarkMode: (v: boolean) => void;
  toggleDarkMode: () => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
};

const DarkModeContext = createContext<DarkModeCtx | undefined>(undefined);

export const DarkModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("darkMode");
      if (stored != null) setIsDarkMode(stored === "true");
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("darkMode", String(isDarkMode));
    } catch {}
    document.documentElement.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);

  const value = useMemo(() => {
    const toggleDarkMode = () => setIsDarkMode((p) => !p);
    const theme: 'light' | 'dark' = isDarkMode ? 'dark' : 'light';
    return {
      isDarkMode,
      setIsDarkMode,
      toggleDarkMode,
      theme,
      toggleTheme: toggleDarkMode,
    };
  }, [isDarkMode]);

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>;
};

export const useDarkMode = () => {
  const ctx = useContext(DarkModeContext);
  if (!ctx) throw new Error("useDarkMode must be used within DarkModeProvider");
  return ctx;
};
